import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Calendar,
  ChevronLeft,
  Clock,
  Copy,
  CreditCard,
  Link as LinkIcon,
  Mail,
  MapPin,
  Phone,
  RotateCcw,
  Tags,
} from 'lucide-react';

import {
  Button,
  Field,
  FieldGrid,
  IconButton,
  PageHeader,
  StatusBadge,
  Surface,
  Text,
} from '@/components/flup';
import BookingDetailsSkeleton from '@/components/skeletons/BookingDetailsSkeleton';
import { toast as newToast } from '@/components/ui/sonner';

import { useAppDispatch, useAppSelector } from '@/hooks/useReduxHooks';
import { RootState } from '@/store';
import { getVendorsBookingsDetailsWithID } from '@/store/thunkMidddleware/turfMiddleware';
import { cancelManualBooking } from '@/store/thunkMidddleware/paymentMiddleware';
import { getSlotTimeRange } from '@/lib/utils';
import routes from '@/routes/routes.json';
import Strings from '@/constants/String';

/**
 * One booking, in full.
 *
 * The money block is the reason this page exists — a vendor opening it is
 * almost always answering "what am I owed and what do I owe". So payment sits
 * in its own bordered card with the two amounts on the same scale, and the
 * destructive action (cancel) is kept below them rather than beside the header
 * where it could be hit by accident.
 */

/**
 * How long past a slot's start the vendor can still cancel it.
 *
 * A customer who rings to say they are not coming rings as the slot begins, not
 * before it — and cancelling is what puts the slot back on sale, so a vendor
 * with someone else waiting needs the option for a while after the clock
 * starts. Reschedule keeps the strict boundary: moving a slot that is already
 * running makes no sense, releasing it does.
 *
 * The server puts no clock on vendor cancellation at all, so this window is the
 * whole rule.
 */
const CANCEL_GRACE_MINUTES = 30;

const money = (n?: number) => `₹${Number(n ?? 0).toFixed(2)}`;

const dateOnly = (value?: string) =>
  value
    ? new Date(value).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';

const BookingDetails = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const detail = useAppSelector(
    (state: RootState) => state.turf?.getVendorsBookingsDetailsWithIDResponse,
  );
  const cancelResponse = useAppSelector(
    (state: RootState) => state.payment.cancelManualBookingResponse,
  );

  const fetchBookingsDetails = (bookingId: string) => {
    dispatch(getVendorsBookingsDetailsWithID({ pathparams: bookingId, page: null, callback: () => undefined }));
  };

  useEffect(() => {
    if (id) fetchBookingsDetails(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // The cancel window is minutes wide, so a page left open would otherwise sit
  // on a countdown that stopped being true — and keep offering the action after
  // it closed.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const tick = setInterval(() => setNowMs(Date.now()), 30000);
    return () => clearInterval(tick);
  }, []);

  if (detail.isLoading) return <BookingDetailsSkeleton />;

  const booking = detail.payload;

  if (!booking) {
    return (
      <Surface padding="p-10" className="max-w-md mx-auto text-center">
        <Text variant="h3" as="p">
          Booking not found
        </Text>
        <Text variant="body" tone="secondary" as="p" className="mt-1.5">
          It may have been removed, or the link is out of date.
        </Text>
        <Button className="mt-5 mx-auto" onClick={() => navigate(-1)}>
          Go back
        </Button>
      </Surface>
    );
  }

  const paidAtVenue = String(booking?.bookingSource?.toUpperCase() || '').trim() === 'VENDOR';
  const scheduledFor = new Date(booking?.scheduledFor);
  const scheduledMs = scheduledFor.getTime();
  const isPast = Number.isNaN(scheduledMs) ? false : nowMs > scheduledMs;
  const status = String(booking?.status || '').toLowerCase();

  // A booking with no usable start time keeps the old behaviour — cancellable —
  // rather than being locked out by arithmetic on a NaN.
  const cancelClosesAt = Number.isNaN(scheduledMs)
    ? Infinity
    : scheduledMs + CANCEL_GRACE_MINUTES * 60 * 1000;
  const graceMinutesLeft = Math.max(0, Math.ceil((cancelClosesAt - nowMs) / 60000));
  /** Inside the slot but still cancellable — worth saying so on the button. */
  const inCancelGrace = isPast && Number.isFinite(cancelClosesAt) && graceMinutesLeft > 0;

  const canCancel =
    status !== 'cancelled' && status !== 'completed' && nowMs < cancelClosesAt;
  const canReschedule =
    !isPast &&
    (booking?.status === Strings.bookingstatus.confirmed ||
      booking?.paymentStatus === Strings.bookingstatus.confirmed);
  const rescheduleExhausted = (booking?.vendorRescheduleCount ?? 0) >= 3;

  const copyLink = async () => {
    if (!booking?.paymentLink) return;
    try {
      await navigator.clipboard.writeText(booking.paymentLink);
      newToast.success('Payment link copied', { closeButton: true });
    } catch {
      newToast.error('Could not copy the link');
    }
  };

  const customerName = `${booking?.userDetails?.firstName || 'Player'} ${
    booking?.userDetails?.lastName || ''
  }`.trim();

  return (
    <div className="space-y-4 max-w-4xl">
      <PageHeader
        title="Booking details"
        count={booking?.bookingRef || booking?._id}
        actions={
          <>
            <Button
              variant="ghost"
              icon={<ChevronLeft className="w-4 h-4" />}
              onClick={() => navigate(-1)}
            >
              Back
            </Button>
            {canReschedule ? (
              <Button
                variant="secondary"
                icon={<RotateCcw className="w-4 h-4" />}
                disabled={rescheduleExhausted}
                title={rescheduleExhausted ? 'This booking has been rescheduled 3 times' : undefined}
                onClick={() =>
                  navigate(`${routes.bookingReschedule}/${id}/${booking?.bookingRef}`)
                }
              >
                Reschedule
              </Button>
            ) : null}
          </>
        }
      >
        <div className="mt-2">
          <StatusBadge status={booking?.status} />
        </div>
      </PageHeader>

      {/* Customer */}
      <Surface padding="p-5">
        <Text variant="fieldLabel" tone="tertiary" as="p" className="mb-3">
          Customer
        </Text>
        <Text variant="h2" as="p">
          {customerName}
        </Text>
        <div className="flex flex-wrap gap-x-6 gap-y-2 mt-3">
          <span className="inline-flex items-center gap-2">
            <Phone className="w-4 h-4 text-flup-ink-3" aria-hidden />
            <Text variant="body" tone="secondary" as="span">
              {booking?.userDetails?.phoneNumber || '—'}
            </Text>
          </span>
          <span className="inline-flex items-center gap-2">
            <Mail className="w-4 h-4 text-flup-ink-3" aria-hidden />
            <Text variant="body" tone="secondary" as="span">
              {booking?.userDetails?.email || '—'}
            </Text>
          </span>
        </div>
      </Surface>

      {/* Booking */}
      <Surface padding="p-5">
        <Text variant="fieldLabel" tone="tertiary" as="p" className="mb-4">
          Booking
        </Text>
        <FieldGrid columns={3}>
          <Field label={<IconLabel icon={<Calendar />}>Date</IconLabel>} value={dateOnly(booking?.scheduledFor)} />
          <Field label={<IconLabel icon={<Clock />}>Time</IconLabel>} value={getSlotTimeRange(booking?.slot)} />
          <Field label={<IconLabel icon={<MapPin />}>Venue</IconLabel>} value={booking?.venueName} />
          <Field label={<IconLabel icon={<Tags />}>Source</IconLabel>} value={booking?.bookingSource} />
          <Field label="Sport" value={booking?.sportsType?.toUpperCase()} />
          <Field label="Booked on" value={dateOnly(booking?.bookingDateAndTime)} />
        </FieldGrid>

        {booking?.paymentLink ? (
          <div className="mt-4 pt-4 border-t border-flup-hairline">
            <Text variant="fieldLabel" tone="tertiary" as="p" className="mb-1.5">
              <IconLabel icon={<LinkIcon />}>Payment link</IconLabel>
            </Text>
            <div className="flex items-center gap-2">
              <a
                href={booking.paymentLink}
                target="_blank"
                rel="noopener noreferrer"
                className="min-w-0 flex-1 truncate"
              >
                <Text variant="caption" tone="info" as="span">
                  {booking.paymentLink}
                </Text>
              </a>
              <IconButton label="Copy payment link" size="sm" onClick={copyLink}>
                <Copy className="w-3.5 h-3.5" />
              </IconButton>
            </div>
          </div>
        ) : null}
      </Surface>

      {/* Payment */}
      <Surface padding="p-5">
        <span className="inline-flex items-center gap-2 mb-4">
          <CreditCard className="w-4 h-4 text-flup-ink-3" aria-hidden />
          <Text variant="fieldLabel" tone="tertiary" as="span">
            Payment
          </Text>
        </span>

        <div className="space-y-3">
          <MoneyRow
            label={paidAtVenue ? 'Paid at venue' : 'Payable at venue'}
            value={money(booking?.paymentSplit?.venuePayableRupee)}
          />
          <MoneyRow
            label="Settlement amount"
            hint="Payment gateway charges apply"
            value={money(booking?.paymentSplit?.vendorAdvanceRupee)}
            tone="success"
          />

          {!paidAtVenue ? (
            <div className="flex items-center justify-between gap-3 pt-3 border-t border-flup-hairline">
              <Text variant="bodyMedium" tone="secondary" as="span">
                Online payment status
              </Text>
              <StatusBadge status={booking?.status} />
            </div>
          ) : null}

          {canCancel ? (
            <div className="flex items-center justify-between gap-3 pt-3 border-t border-flup-hairline">
              <div className="min-w-0">
                <Text variant="bodyMedium" as="p">
                  Cancel this booking
                </Text>
                <Text variant="micro" tone="tertiary" as="p" className="mt-0.5">
                  {inCancelGrace
                    ? `The slot has started — you have ${graceMinutesLeft} min left to release it for someone else.`
                    : 'The slot goes back on sale immediately.'}
                </Text>
              </div>
              <Button
                variant="danger"
                loading={cancelResponse.isLoading}
                onClick={() =>
                  dispatch(
                    cancelManualBooking({
                      pathparams: booking?._id,
                      callback: (res: any) => res?.payload && id && fetchBookingsDetails(id),
                    }),
                  )
                }
              >
                Cancel booking
              </Button>
            </div>
          ) : null}
        </div>
      </Surface>
    </div>
  );
};

/** A field label with its icon, keeping the icon out of every call site. */
const IconLabel: React.FC<{ icon: React.ReactNode; children: React.ReactNode }> = ({
  icon,
  children,
}) => (
  <span className="inline-flex items-center gap-1.5">
    <span className="[&_svg]:w-3 [&_svg]:h-3 text-flup-ink-3">{icon}</span>
    {children}
  </span>
);

const MoneyRow: React.FC<{
  label: string;
  hint?: string;
  value: string;
  tone?: 'primary' | 'success';
}> = ({ label, hint, value, tone = 'primary' }) => (
  <div className="flex items-start justify-between gap-3">
    <div className="min-w-0">
      <Text variant="bodyMedium" tone="secondary" as="p">
        {label}
      </Text>
      {hint ? (
        <Text variant="micro" tone="tertiary" as="p" className="mt-0.5">
          {hint}
        </Text>
      ) : null}
    </div>
    <Text
      variant="statSm"
      as="span"
      className={tone === 'success' ? 'text-flup-success shrink-0' : 'shrink-0'}
    >
      {value}
    </Text>
  </div>
);

export default BookingDetails;
